import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// Get API base URL from environment variables
// Note: webpack DefinePlugin replaces process.env.VARIABLE_NAME with actual string values at build time
// After replacement by webpack, this becomes: const API_BASE_URL = "https://launchpad-dev-api.servify.tech";
// If webpack doesn't replace it, it will be undefined (process doesn't exist in browser)
// @ts-ignore - process.env is replaced at build time by webpack DefinePlugin
const API_BASE_URL: string | undefined = (function() { try { return process.env.API_BASE_URL; } catch { return undefined; } })();

/**
 * Normalize a URL by removing trailing slashes
 */
function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * Join base URL and path, ensuring no double slashes or duplicate /api
 * Exported for use in other files that construct URLs manually
 * 
 * Handles two scenarios:
 * 1. API_BASE_URL includes /api (e.g., https://example.com/api) → paths should NOT include /api
 * 2. API_BASE_URL doesn't include /api (e.g., https://api.example.com) → paths need /api added
 */
export function joinUrl(baseUrl: string, path: string): string {
  const normalizedBase = normalizeUrl(baseUrl);
  let normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Remove duplicate /api prefix if base URL already ends with /api and path starts with /api
  if (normalizedBase.endsWith('/api') && normalizedPath.startsWith('/api/')) {
    normalizedPath = normalizedPath.substring(4); // Remove '/api' prefix, keep the '/'
  }
  // Add /api prefix if base URL doesn't end with /api and path doesn't start with /api
  // This ensures all API calls go to /api/* routes on the backend
  else if (!normalizedBase.endsWith('/api') && !normalizedPath.startsWith('/api/') && !normalizedPath.startsWith('/health') && !normalizedPath.startsWith('/webhooks')) {
    normalizedPath = `/api${normalizedPath}`;
  }
  
  return `${normalizedBase}${normalizedPath}`;
}

export function getApiBaseUrl(): string {
  // Use the API_BASE_URL that webpack DefinePlugin has replaced with actual value
  const apiBaseUrl = API_BASE_URL;
  
  // Debug: Always log to help diagnose the issue
  console.log('🔧 API Config:', { 
    apiBaseUrl,
    using: apiBaseUrl || 'relative URLs (FALLBACK)'
  });
  
  // If webpack didn't replace the value, apiBaseUrl will be undefined
  // This means the build wasn't done with BUILD_ENV=staging
  if (!apiBaseUrl) {
    console.error('❌ API_BASE_URL is undefined! This means webpack DefinePlugin did not replace process.env.API_BASE_URL');
    console.error('   Expected: Value from environment file (e.g., http://localhost:5000/api)');
    console.error('   Solution: Rebuild with BUILD_ENV=staging or restart dev server');
    // Return empty string to use relative URLs (fallback)
    return '';
  }
  
  // Normalize the URL to remove trailing slashes
  // This prevents double slashes when joining with paths
  return normalizeUrl(apiBaseUrl);
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  headers?: Record<string, string>
): Promise<Response> {
  const isFormData = data instanceof FormData;
  const baseUrl = getApiBaseUrl();
  const fullUrl = baseUrl ? joinUrl(baseUrl, url) : url;
  
  const res = await fetch(fullUrl, {
    method,
    headers: isFormData 
      ? { ...headers } // Don't set Content-Type for FormData, let browser set it
      : data 
        ? { "Content-Type": "application/json", ...headers }
        : { ...headers },
    body: isFormData ? data : data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const baseUrl = getApiBaseUrl();
    const fullUrl = baseUrl ? joinUrl(baseUrl, url) : url;
    
    const res = await fetch(fullUrl, {
      credentials: "include",
    });

    // Handle 401 (Unauthorized) based on behavior setting
    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
      // For "throw" behavior, create a proper error with status code
      const errorText = await res.text().catch(() => res.statusText);
      const error = new Error(`${res.status}: ${errorText || 'Unauthorized'}`);
      (error as any).status = 401;
      throw error;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes (changed from Infinity to allow refetch after SSO)
      retry: (failureCount, error: any) => {
        // Don't retry on 401 (unauthorized)
        if (error?.status === 401 || error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
          return false;
        }
        // Retry other errors (like 400 Bad Request) up to 2 times
        // This helps with timing issues after SSO login
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000), // Exponential backoff
    },
    mutations: {
      retry: false,
    },
  },
});
